import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import Button from "./Button";
import Field from "./Field";
import LoginWrapper from "./LoginWrapper";
import "./login.css";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const LoginPage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { login, isAuthenticated, loading } = useAuth();
    
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [isLoggingIn, setIsLoggingIn] = useState(false);
    const [isGoogleLoading, setIsGoogleLoading] = useState(false);

    // Show error from URL params (OAuth errors)
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const errorParam = params.get('error');
        const emailParam = params.get('email');
        
        if (errorParam === 'oauth_cancelled') {
            setError('Google sign-in was cancelled');
        } else if (errorParam === 'not_authorized') {
            setError(emailParam 
                ? `Your email (${emailParam}) is not authorized to access this application`
                : 'Your email is not authorized to access this application'
            );
        } else if (errorParam === 'auth_failed') {
            setError('Authentication failed. Please try again.');
        } else if (location.state?.error) {
            setError(location.state.error);
        }
    }, [location]);

    // Redirect if already authenticated
    useEffect(() => {
        if (!loading && isAuthenticated) {
            const from = location.state?.from?.pathname || "/";
            navigate(from, { replace: true });
        }
    }, [isAuthenticated, loading, navigate, location]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError("");
        setIsLoggingIn(true);

        try {
            await login(email, password);
            const from = location.state?.from?.pathname || "/";
            navigate(from, { replace: true });
        } catch (err) {
            setError(err.message || "Login failed. Please try again.");
        } finally {
            setIsLoggingIn(false);
        }
    };

    // Handle Google OAuth login
    const handleGoogleLogin = async () => {
        setError("");
        setIsGoogleLoading(true);
        
        try {
            // Get authorization URL from backend
            const response = await fetch(`${API}/auth/google/custom/login`, {
                method: 'GET',
                credentials: 'include'
            });
            
            if (!response.ok) {
                throw new Error('Failed to initiate Google login');
            }
            
            const data = await response.json();
            
            // Redirect to Google OAuth
            console.log('Redirecting to Google OAuth...');
            window.location.href = data.authorization_url;
            
        } catch (err) {
            console.error('Google login error:', err);
            setError('Failed to start Google sign-in. Please try again.');
            setIsGoogleLoading(false);
        }
    };

    // Show loading while checking initial auth state
    if (loading) {
        return (
            <div className="min-h-screen bg-login-black flex items-center justify-center login-page">
                <div className="flex flex-col items-center gap-4">
                    <div className="w-8 h-8 border-2 border-[#7ae2cf] border-t-transparent rounded-full animate-spin" />
                    <span className="text-description text-sm">Loading...</span>
                </div>
            </div>
        );
    }

    return (
        <LoginWrapper>
            {/* Google Sign In Button */}
            <Button 
                className="w-full gap-2 bg-line" 
                isSecondary
                onClick={handleGoogleLogin}
                disabled={isGoogleLoading}
                type="button"
            >
                {isGoogleLoading ? (
                    <>
                        <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                        Connecting...
                    </>
                ) : (
                    <>
                        <img
                            className="size-6 opacity-100"
                            src="/images/google.svg"
                            width={24}
                            height={24}
                            alt="Google"
                        />
                        Sign in with Google
                    </>
                )}
            </Button>
            
            {/* Divider */}
            <div className="my-6 text-center text-description-2 text-description">
                Or sign in with email
            </div>
            
            <form onSubmit={handleSubmit} data-testid="login-form">
                {error && (
                    <div 
                        className="mb-4 p-3 rounded-lg bg-red-500/20 border border-red-500/50 text-red-400 text-sm"
                        data-testid="login-error"
                    >
                        {error}
                    </div>
                )}
                <Field
                    className="mb-4"
                    label="Email"
                    placeholder="Enter your email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    data-testid="login-email-input"
                />
                <Field
                    className="mb-6"
                    label="Password"
                    placeholder="Enter your password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    forgotPassword={false}
                    data-testid="login-password-input"
                />
                <Button
                    className="w-full"
                    isPrimary
                    type="submit"
                    disabled={isLoggingIn || !email || !password}
                    data-testid="login-submit-button"
                >
                    {isLoggingIn ? "Signing in..." : "Sign in"}
                </Button>
            </form>
            <div className="mt-6 text-center text-description-3 text-description">
                Admin access only
            </div>
        </LoginWrapper>
    );
};

export default LoginPage;
